import { SETUP_STEPS, setupStepState, type SetupProgress, type SetupStepId, type SetupStepState } from "./setupSteps.js";

const nodeClasses: Record<SetupStepState, string> = {
  complete: "border-status-active-border bg-status-active-bg text-status-active-text",
  current: "border-accent-strong bg-accent-strong text-on-accent",
  locked: "border-border-default bg-surface-overlay text-content-muted",
};

const labelClasses: Record<SetupStepState, string> = {
  complete: "text-content-primary",
  current: "text-content-primary",
  locked: "text-content-muted",
};

function CheckMark() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="h-4 w-4" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 12.5l4.5 4.5L19 7" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-3.5 w-3.5" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 10V7.5a5 5 0 0110 0V10M6 10h12a1 1 0 011 1v8a1 1 0 01-1 1H6a1 1 0 01-1-1v-8a1 1 0 011-1z" />
    </svg>
  );
}

/**
 * The setup spine, rendered bare at the top of the Setup page — no card around
 * it, because it frames the step content below rather than sitting beside it.
 * For an agent it is a read-out, not a control: they move through setup by
 * finishing the step in front of them, not by clicking around the bar. Locked
 * steps are shown rather than hidden, so the whole path is visible from day one.
 *
 * `onSelectStep` turns every node into a button, including locked ones. Only
 * staff impersonating an agent get that — see PortalSetupPage.
 */
export function SetupProgressBar({
  progress,
  selectedId,
  onSelectStep,
}: {
  progress: SetupProgress;
  /** The step whose content is on screen — highlighted even when complete. */
  selectedId?: SetupStepId;
  onSelectStep?: (id: SetupStepId) => void;
}) {
  const completedCount = SETUP_STEPS.filter((step) => progress[step.id]).length;
  const allDone = completedCount === SETUP_STEPS.length;
  // The track fills from the first node to the last, so each completed step is
  // worth one gap rather than one node.
  const fillPercent = allDone
    ? 100
    : (completedCount / Math.max(SETUP_STEPS.length - 1, 1)) * 100;

  return (
    <nav aria-label="Setup progress">
      <ol
        className="relative grid gap-2"
        style={{ gridTemplateColumns: `repeat(${SETUP_STEPS.length}, minmax(0, 1fr))` }}
      >
        {/* Track sits behind the nodes and spans centre-to-centre of the first
            and last node, so the fill lines up with the circles however many
            steps there are — each node's centre is half a column in. */}
        <div
          className="pointer-events-none absolute top-5 h-1 -translate-y-1/2 rounded-full bg-surface-muted"
          style={{ left: `${50 / SETUP_STEPS.length}%`, right: `${50 / SETUP_STEPS.length}%` }}
          aria-hidden
        >
          <div
            className="h-full rounded-full bg-accent-strong transition-[width] duration-300 motion-reduce:transition-none"
            style={{ width: `${Math.min(100, Math.max(0, fillPercent))}%` }}
          />
        </div>

        {SETUP_STEPS.map((step, index) => {
          const state = setupStepState(index, progress);
          const selected = step.id === selectedId;

          const body = (
            <>
              <span
                className={`relative z-10 grid h-10 w-10 place-items-center rounded-full border-2 text-sm font-extrabold ${nodeClasses[state]} ${
                  selected ? "ring-2 ring-accent-strong ring-offset-2 ring-offset-surface-page" : ""
                }`}
              >
                {state === "complete" ? <CheckMark /> : state === "locked" ? <LockIcon /> : index + 1}
              </span>
              <span className="mt-3 block px-1 text-center">
                <span className="block text-[10px] font-bold uppercase tracking-[0.14em] text-content-muted">
                  Step {index + 1}
                </span>
                <span className={`mt-1 block text-[11px] font-bold leading-4 sm:text-xs ${labelClasses[state]}`}>
                  {step.label}
                </span>
              </span>
            </>
          );

          return (
            <li key={step.id} className="flex flex-col items-center">
              {onSelectStep ? (
                <button
                  type="button"
                  onClick={() => onSelectStep(step.id)}
                  className="focus-ring flex w-full flex-col items-center rounded-xl py-1 transition hover:opacity-80"
                  aria-current={selected ? "step" : undefined}
                >
                  {body}
                </button>
              ) : (
                <div
                  className="flex w-full flex-col items-center py-1"
                  aria-current={selected ? "step" : undefined}
                >
                  {body}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
